/**
 * Triangle budgets (05 §5 rule 2) and the class each prop kind is held to. The kit test
 * enforces these, so a builder that grows past its class fails CI rather than the frame rate.
 */

import type { PropKind } from '../../sim/types';

export const TRIANGLE_BUDGET = { small: 60, tree: 120, structure: 600, vehicle: 900, character: 350 } as const;

/**
 * The thinnest a bar, wire or strut may be modelled (05 §5 rule 3: "at 16 px/m a detail under 12 cm
 * is sub-pixel"). At the ground zoom of 18 px/m this is 2.3 px across, so it survives the palette
 * lock and the outline pass as a solid line instead of breaking into a string of dots while the
 * camera scrolls. Nothing that has to READ may be modelled below it - it is cheaper to drop it.
 */
export const MIN_BAR_METRES = 0.13;
export type BudgetClass = keyof typeof TRIANGLE_BUDGET;

/**
 * Run kinds (`wall`, `wire`, `hedge`...) grow with their length; they are held to the
 * structure budget at up to this many metres, which covers every compound-scale run. A run
 * across the whole map is more triangles but still one merged, static draw call.
 */
export const RUN_BUDGET_LENGTH = 16;

export const PROP_BUDGET: Record<PropKind, BudgetClass> = {
  pine: 'tree', broadleaf: 'tree', palm: 'tree', deadTree: 'tree', cactus: 'tree', shrub: 'tree',
  rock: 'small', stump: 'small', snowDrift: 'small', tankTrap: 'small', proximityPost: 'small',
  crate: 'small', barrel: 'small', alarmPost: 'small', lever: 'small', controlsPyramid: 'small', fieldGrave: 'small',
  hedge: 'structure', sandbags: 'structure', wall: 'structure', wire: 'structure', ruinWall: 'structure',
  pipeline: 'structure', bridge: 'structure', rail: 'structure',
  bunker: 'structure', mgNest: 'structure', watchtower: 'structure', searchlight: 'structure',
  turretBunker: 'structure', mannedTurret: 'structure',
  hut: 'structure', tent: 'structure', guardHouse: 'structure', barracks: 'structure', ruinHouse: 'structure',
  commsPost: 'structure', hangar: 'structure', prison: 'structure',
  fuelTank: 'structure', ammoShack: 'structure', magazine: 'structure', radar: 'structure',
  samLauncher: 'structure', pumpStation: 'structure', tigerPit: 'structure',
  aircraft: 'vehicle',
  // The expansion (10 §3). Only the two on rails and the launcher need the vehicle class; the barge, the staff
  // car and the crash site fit a structure's. The breaker panel is a cabinet with a lever, a lamp and three
  // insulator stacks - a dozen parts, which is a structure, not a small prop. The four true markers are small.
  bridgePier: 'structure', relayMast: 'structure', casemate: 'structure', generatorHut: 'structure',
  locomotive: 'vehicle', fuelWagon: 'vehicle', mobileLauncher: 'vehicle',
  barge: 'structure', staffCar: 'structure', crashSite: 'structure', fieldGun: 'structure', gunPit: 'structure',
  hqBuilding: 'structure', cellBlock: 'structure', laboratory: 'structure', telephoneExchange: 'structure',
  partisanCamp: 'structure', breakerPanel: 'structure', safe: 'small',
  overlookMarker: 'small', observationPost: 'small', culvert: 'small', strikeBeacon: 'small',
};

/**
 * Models from the M0 look-dev that were signed off by eye before this table existed and sit
 * over their class. They are frozen at the count they have - changing an approved silhouette
 * to win back forty triangles is a bad trade - so they cannot grow, and nothing new may join
 * them. Trees are instanced and clutter is merged, so none of this costs a draw call.
 */
export const LEGACY_TRIANGLES: Partial<Record<PropKind, number>> = {
  pine: 132, broadleaf: 148, palm: 150, rock: 64, barrel: 96, alarmPost: 76,
};
